import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useApolloClient, useMutation} from '@apollo/client';
import {Alert, Button, Input, Select, Spin} from 'antd';
import {toast} from 'react-toastify';
import {ActionButton, TechnicalDetails, Tip} from '../shared/ui';
import {actionError, unionError} from '../shared/errors';
import ScopeQuestions, {answersProblem} from './ScopeQuestions';
import {
  ATTACH_NEWS_SOURCE_COVERAGE,
  CREATE_NEWS_SOURCE,
  GET_NEWS_SOURCE,
  PROBE_NEWS_SOURCE,
} from './resolvers';
import {ADD, GNEWS_EDITIONS, LANGUAGES, TOAST} from './copy';
import {
  coverOf,
  coverageFromAnswers,
  exchangeCode,
  hostOf,
  nameFromHost,
  normalizeAddress,
  readsOtherSitesFeed,
  reliabilityOf,
  summarySentence,
} from './sourceHelpers';
import styles from '../shared/news.module.scss';

const FEW_ARTICLES = /google news/i;

// Three steps, and nothing is saved until the last one:
// which website → is it the right kind of news → save.
const AddWebsiteModal = ({exchange, defaults, canCreate, onSaved}) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const x = exchangeCode(exchange);

  const [step, setStep] = useState('address');
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [probe, setProbe] = useState(null);
  const [existing, setExisting] = useState(null);
  const [feedUrl, setFeedUrl] = useState('');
  const [gnewsLocale, setGnewsLocale] = useState(defaults.gnewsLocale);
  const [language, setLanguage] = useState(defaults.language);
  const [showMore, setShowMore] = useState(false);
  const [name, setName] = useState('');
  const [answers, setAnswers] = useState({
    cover: 'all',
    keywords: defaults.keywords,
    section: undefined,
    reliability: 'established',
  });

  const onError = actionError('save this website');
  const [createSource, {loading: creating}] = useMutation(CREATE_NEWS_SOURCE, {
    onError,
  });
  const [attach, {loading: attaching}] = useMutation(
    ATTACH_NEWS_SOURCE_COVERAGE,
    {onError},
  );

  const homepageUrl = normalizeAddress(address);
  const host = probe?.host || hostOf(homepageUrl);
  const displayName = existing?.name || name;
  const alreadyHere = existing?.coverage?.some(
    row => row.exchangeId === exchange.id,
  );
  const mustPickSection = readsOtherSitesFeed(homepageUrl, probe?.feedUrl);
  const canRead = Boolean(probe?.resolvedMode) || Boolean(existing);
  const fewArticles = (probe?.warnings || []).some(w => FEW_ARTICLES.test(w));
  const otherWarnings = (probe?.warnings || []).filter(
    w => !FEW_ARTICLES.test(w),
  );
  const problem = answersProblem(answers, mustPickSection);

  const check = async () => {
    if (!homepageUrl) return setAddressError(ADD.addressInvalid);
    setAddressError(null);
    setChecking(true);
    try {
      const {data} = await client.query({
        query: PROBE_NEWS_SOURCE,
        variables: {
          homepageUrl,
          gnewsLocale,
          feedUrl: normalizeAddress(feedUrl) || undefined,
        },
        fetchPolicy: 'no-cache',
      });
      const result = data?.probeNewsSource;
      if (result?.__typename !== 'NewsSourceProbeResult') {
        return unionError('check this website', result);
      }
      let found = null;
      if (result.alreadyRegistered && result.existingSourceId) {
        const {data: sourceData} = await client.query({
          query: GET_NEWS_SOURCE,
          variables: {sourceId: result.existingSourceId},
          fetchPolicy: 'no-cache',
        });
        found =
          sourceData?.newsSource?.__typename === 'RegisteredNewsSource'
            ? sourceData.newsSource
            : null;
      }
      // A website already set up elsewhere keeps its kind of coverage and
      // its reliability; everything else starts from the defaults.
      const elsewhere = found?.coverage?.[0];
      if (elsewhere) {
        setAnswers(prev => ({
          ...prev,
          cover: coverOf(elsewhere) === 'market' ? 'market' : 'all',
          reliability: reliabilityOf(elsewhere.trustWeight),
        }));
        setLanguage(found.defaultLanguage || language);
      }
      if (result.warnings?.some(w => FEW_ARTICLES.test(w))) setShowMore(true);
      setProbe(result);
      setExisting(found);
      setName(prev => prev || nameFromHost(result.host));
      setStep('questions');
    } catch (err) {
      actionError('check this website')(err);
    } finally {
      setChecking(false);
    }
  };

  const coverage = () => ({
    exchangeId: exchange.id,
    exchangeCode: exchange.code,
    exchangeName: exchange.stockExchangeName,
    enabled: true,
    gnewsLocale,
    ...coverageFromAnswers(answers),
  });

  const finish = message => {
    toast.success(message);
    onSaved();
  };

  const save = async () => {
    if (existing) {
      const {data} = await attach({
        variables: {sourceId: existing.id, coverage: coverage()},
      });
      const result = data?.attachNewsSourceCoverage;
      if (!result) return;
      if (result.__typename === 'Error') {
        return unionError('save this website', result);
      }
      return finish(TOAST.added(existing.name, x));
    }
    const {data} = await createSource({
      variables: {
        data: {
          name: displayName.trim(),
          homepageUrl,
          defaultLanguage: language,
          enabled: true,
          coverage: [coverage()],
          // Hand back what the check found so the website isn't checked twice.
          // A feed address someone typed is kept; anything else stays automatic.
          discovery: {
            mode: feedUrl ? probe.resolvedMode : 'AUTO',
            resolvedMode: probe.resolvedMode,
            feedUrl: probe.feedUrl || undefined,
            sitemapUrl: probe.sitemapUrl || undefined,
          },
        },
      },
    });
    const result = data?.createNewsSource;
    if (!result) return;
    if (result.__typename === 'Error') {
      return unionError('save this website', result);
    }
    finish(TOAST.added(result.name, x));
  };

  // Saved switched off, so it shows as "Waiting for tech team" until they
  // finish the setup.
  const sendToTech = async () => {
    const {data} = await createSource({
      variables: {
        data: {
          name: displayName.trim(),
          homepageUrl,
          defaultLanguage: language,
          enabled: false,
          coverage: [coverage()],
          discovery: {mode: 'AUTO'},
        },
      },
    });
    const result = data?.createNewsSource;
    if (!result) return;
    if (result.__typename === 'Error') {
      return unionError('save this website', result);
    }
    finish(TOAST.sentToTech(result.name));
  };

  const moreOptions = (
    <div className={styles.block}>
      <Button
        type="link"
        className={styles.linkButton}
        onClick={() => setShowMore(!showMore)}>
        <Tip
          label={`${showMore ? '▾' : '▸'} ${ADD.moreOptions}`}
          tip={ADD.moreOptionsTip}
        />
      </Button>
      {showMore ? (
        <div className={styles.formGrid}>
          <label>
            <Tip label={ADD.language} tip={ADD.languageTip} />
            <Select
              className={styles.fullWidth}
              value={language}
              onChange={setLanguage}
              options={LANGUAGES}
            />
          </label>
          <label>
            <Tip label={ADD.gnews} tip={ADD.gnewsTip} />
            <Select
              className={styles.fullWidth}
              value={gnewsLocale}
              onChange={setGnewsLocale}
              options={GNEWS_EDITIONS}
            />
          </label>
        </div>
      ) : null}
    </div>
  );

  if (step === 'address') {
    return (
      <div className={styles.modalRoot}>
        <h2>{ADD.title(x)}</h2>
        <label htmlFor="website-address">
          <Tip label={ADD.address} tip={ADD.addressTip} />
        </label>
        <Input
          id="website-address"
          value={address}
          placeholder={ADD.addressPlaceholder}
          onChange={e => setAddress(e.target.value)}
          onPressEnter={check}
          disabled={checking}
          status={addressError ? 'error' : undefined}
        />
        {addressError ? <p className={styles.reason}>{addressError}</p> : null}
        {checking ? (
          <p className={styles.reason}>
            <Spin size="small" /> {ADD.checking}
          </p>
        ) : null}
        <div className={styles.modalActions}>
          <ActionButton
            type="primary"
            allowed={canCreate}
            tip={ADD.checkTip}
            loading={checking}
            onClick={check}>
            {ADD.check}
          </ActionButton>
        </div>
      </div>
    );
  }

  if (step === 'questions') {
    return (
      <div className={styles.modalRoot}>
        <h2>{ADD.title(x)}</h2>

        {alreadyHere ? (
          <>
            <Alert
              type="info"
              showIcon
              className={styles.block}
              message={ADD.alreadyHere(existing.name, x)}
            />
            <div className={styles.modalActions}>
              <Button onClick={() => setStep('address')}>{ADD.back}</Button>
              <Button
                type="primary"
                onClick={() =>
                  navigate(`/dashboard/news/sources/${existing.id}`)
                }>
                {ADD.openPage}
              </Button>
            </div>
          </>
        ) : (
          <>
            {existing ? (
              <Alert
                type="info"
                showIcon
                className={styles.block}
                message={ADD.alreadyElsewhere(
                  existing.name,
                  existing.coverage
                    .map(row => row.exchangeCode || row.exchangeName)
                    .join(' and '),
                  x,
                )}
              />
            ) : canRead ? (
              <Alert
                type="success"
                showIcon
                className={styles.block}
                message={ADD.canRead(displayName)}
              />
            ) : (
              <Alert
                type="warning"
                showIcon
                className={styles.block}
                message={ADD.cantRead}
              />
            )}
            {probe?.previouslyDeleted ? (
              <Alert
                type="info"
                showIcon
                className={styles.block}
                message={ADD.previouslyDeleted}
              />
            ) : null}
            {fewArticles ? (
              <Alert
                type="warning"
                showIcon
                className={styles.block}
                message={ADD.fewArticles}
              />
            ) : null}

            {canRead ? (
              <ScopeQuestions
                value={answers}
                onChange={setAnswers}
                exchange={x}
                host={host}
                samples={probe?.sampleArticles}
              />
            ) : null}

            {moreOptions}

            <TechnicalDetails>
              <label htmlFor="feed-address">
                <Tip label={ADD.feedAddress} tip={ADD.feedAddressTip} />
              </label>
              <Input
                id="feed-address"
                value={feedUrl}
                placeholder="https://www.example.com/feed"
                onChange={e => setFeedUrl(e.target.value)}
              />
              {probe?.attempts?.length ? (
                <>
                  <p className={styles.muted}>{ADD.checkResults}</p>
                  <ul className={styles.articles}>
                    {probe.attempts.map((attempt, i) => (
                      <li key={`${attempt.rung}-${i}`}>
                        <span>
                          {attempt.rung} · {attempt.url}
                        </span>
                        <span className={styles.skipped}>
                          {attempt.status ?? '-'} · {attempt.itemCount ?? 0}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {otherWarnings.map(warning => (
                <p key={warning} className={styles.muted}>
                  {warning}
                </p>
              ))}
            </TechnicalDetails>

            {canRead && problem ? (
              <Alert
                type="warning"
                showIcon
                className={styles.block}
                message={problem}
              />
            ) : null}

            <div className={styles.modalActions}>
              <Button onClick={() => setStep('address')}>{ADD.back}</Button>
              <Button loading={checking} onClick={check}>
                {ADD.checkAgain}
              </Button>
              {canRead ? (
                <Button
                  type="primary"
                  disabled={Boolean(problem)}
                  onClick={() => setStep('save')}>
                  {ADD.next}
                </Button>
              ) : (
                <ActionButton
                  type="primary"
                  allowed={canCreate}
                  tip={ADD.sendToTechTip}
                  loading={creating}
                  onClick={sendToTech}>
                  {ADD.sendToTech}
                </ActionButton>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.modalRoot}>
      <h2>{ADD.summaryTitle}</h2>
      {existing ? null : (
        <div className={styles.block}>
          <label htmlFor="website-name">
            <Tip label={ADD.name} tip={ADD.nameTip} />
          </label>
          <Input
            id="website-name"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
      )}
      <p>{summarySentence(displayName, x, answers)}</p>
      <div className={styles.modalActions}>
        <Button onClick={() => setStep('questions')}>{ADD.back}</Button>
        <ActionButton
          type="primary"
          allowed={canCreate}
          tip={ADD.saveTip}
          disabled={!displayName.trim()}
          loading={creating || attaching}
          onClick={save}>
          {existing ? ADD.saveToExchange(x) : ADD.save}
        </ActionButton>
      </div>
    </div>
  );
};

export default AddWebsiteModal;
